/**
 * État d'un entretien d'implémentation tel que l'écran le lit (POC §6, §10). L'écran n'invente rien :
 * il affiche l'état structuré que lui rend le moteur — faits, écarts, readiness, recommandations,
 * question en cours et ses raisons. La readiness est **calculée** ici à partir des faits et d'un
 * profil déclaré (POC §13), jamais lue d'un modèle.
 */

export type DomainKey =
  | 'company'
  | 'organization'
  | 'products'
  | 'customers'
  | 'sales'
  | 'purchasing'
  | 'inventory'
  | 'production'
  | 'quality'
  | 'traceability'
  | 'accounting'
  | 'systems';

/** Nature du fait (155 §3, ADR-071) et validation : deux axes distincts (plan §2 C2). */
export type EpistemicStatus = 'declared' | 'observed' | 'inferred_ai' | 'hypothetical';
export type ValidationStatus = 'unvalidated' | 'confirmed' | 'rejected' | 'superseded';

export type SourceKind = 'official_registry' | 'company_website' | 'booking_form' | 'interview' | 'customer_correction' | 'ai_inference';

export interface UnderstandingItem {
  id: string;
  domain: DomainKey;
  label: string;
  value?: string;
  epistemic_status?: EpistemicStatus;
  validation_status?: ValidationStatus;
  source?: SourceKind;
  /** Confiance rapportée par le fournisseur : informative, jamais une probabilité (ADR-071 §5). */
  confidence?: number;
  /** Phrase du client ou de la source qui fonde la valeur. */
  statement?: string;
  /** Pour une déduction : ce qui la fonde. */
  reason?: string;
  /** Faits en contradiction avec celui-ci. */
  conflicts_with?: string[];
  deferred?: boolean;
}

/** Les cinq libellés de l'écran (POC §10.4) : une projection des deux axes, jamais un état stocké. */
export type DisplayStatus = 'confirmed' | 'inferred' | 'to_confirm' | 'missing' | 'conflicting';

export function displayStatus(item: UnderstandingItem): DisplayStatus {
  if (item.value === undefined || item.validation_status === 'rejected') return 'missing';
  if ((item.conflicts_with?.length ?? 0) > 0) return 'conflicting';
  if (item.validation_status === 'confirmed') return 'confirmed';
  if (item.epistemic_status === 'inferred_ai') return 'inferred';
  // Une déclaration du client pendant l'entretien vaut confirmation ; registre, site et formulaire restent à confirmer.
  if (item.epistemic_status === 'declared' && (item.source === 'interview' || item.source === 'customer_correction')) return 'confirmed';
  return 'to_confirm';
}

export type RecommendationKind = 'replace' | 'integrate' | 'keep' | 'ignore' | 'review';

export interface Recommendation {
  id: string;
  domain: DomainKey;
  title: string;
  current: string;
  recommendation: RecommendationKind[];
  /** Issue probable quand la recommandation est « à étudier ». */
  likely?: RecommendationKind;
  rationale: string;
  /** Faits qui fondent la recommandation. */
  evidence_ids: string[];
  confidence: number;
  decision?: 'keep_current' | 'study_ayumia' | 'accepted';
}

export interface QuestionCandidate {
  id: string;
  text: string;
  objective: string;
  targeted: string[];
  information_gain: number;
  importance: number;
  impact: number;
  uncertainty: number;
  dependency: number;
  redundancy: number;
  fatigue: number;
}

/** Formule du POC §7.3, appliquée telle quelle : inspectable et testable. */
export function questionPriority(candidate: QuestionCandidate): number {
  const product = candidate.information_gain * candidate.importance * candidate.impact * candidate.uncertainty * candidate.dependency;
  return Math.round((product - candidate.redundancy - candidate.fatigue) * 1000) / 1000;
}

export interface ChatMessage {
  id: string;
  role: 'ai' | 'customer' | 'consultant' | 'system';
  text: string;
  at: string;
  /** Question posée par ce message, pour « Pourquoi cette question ? ». */
  question_id?: string;
}

/* Readiness déterministe (POC §13 ; plan §3.4). */

export interface ReadinessRequirement {
  item: string;
  weight: number;
  blocking?: boolean;
}

export type ReadinessProfile = Readonly<Partial<Record<DomainKey, readonly ReadinessRequirement[]>>>;

/** Satisfaction par statut affiché ; coefficients du profil, pas du code métier. */
export const SATISFACTION: Readonly<Record<DisplayStatus, number>> = { confirmed: 1, to_confirm: 0.6, inferred: 0.3, missing: 0, conflicting: 0 };

export interface DomainReadiness {
  domain: DomainKey;
  score: number;
  covered: string[];
  partial: string[];
  missing: string[];
  blocking: string[];
}

export interface Readiness {
  overall: number;
  domains: DomainReadiness[];
  blockers: string[];
}

export function computeReadiness(profile: ReadinessProfile, items: readonly UnderstandingItem[], scope: readonly DomainKey[]): Readiness {
  const byId = new Map(items.map((item) => [item.id, item]));
  const domains: DomainReadiness[] = [];
  const blockers: string[] = [];
  let weighted = 0;
  let weights = 0;
  for (const domain of scope) {
    const requirements = profile[domain] ?? [];
    if (requirements.length === 0) continue;
    let total = 0;
    let sum = 0;
    const result: DomainReadiness = { domain, score: 0, covered: [], partial: [], missing: [], blocking: [] };
    for (const requirement of requirements) {
      const item = byId.get(requirement.item);
      const status = item === undefined ? 'missing' : displayStatus(item);
      const satisfaction = SATISFACTION[status];
      total += requirement.weight;
      sum += requirement.weight * satisfaction;
      const label = item?.label ?? requirement.item;
      if (satisfaction === 1) result.covered.push(label);
      else if (satisfaction > 0) result.partial.push(label);
      else result.missing.push(label);
      if ((requirement.blocking === true && satisfaction < 0.6) || status === 'conflicting') {
        result.blocking.push(label);
        blockers.push(label);
      }
    }
    let score = total === 0 ? 0 : sum / total;
    // Un point bloquant plafonne le domaine : un pourcentage ne masque jamais un blocage (41 §6).
    if (result.blocking.length > 0) score = Math.min(score, 0.5);
    result.score = Math.round(score * 100);
    domains.push(result);
    weighted += result.score * requirements.length;
    weights += requirements.length;
  }
  return { overall: weights === 0 ? 0 : Math.round(weighted / weights), domains, blockers };
}

export interface InterviewState {
  status: 'in_progress' | 'ready_for_review' | 'review';
  company: string;
  messages: ChatMessage[];
  items: UnderstandingItem[];
  recommendations: Recommendation[];
  scope: DomainKey[];
  readiness: Readiness;
  /** Readiness au début de l'entretien, pour « progression depuis le début ». */
  initial_overall: number;
  current_question?: { candidate: QuestionCandidate; candidates: QuestionCandidate[] };
  /** Domaine exploré en ce moment. */
  focus?: DomainKey;
  consultant_present: boolean;
  /** Réponse type proposée en démonstration seulement. */
  suggested_reply?: string;
  telemetry: { provider: string; model: string; calls: number; input_tokens: number; output_tokens: number; latency_ms: number };
  simulated: boolean;
}

export interface InterviewEngine {
  start(): Promise<InterviewState>;
  answer(text: string): Promise<InterviewState>;
  defer(kind: 'dont_know' | 'later'): Promise<InterviewState>;
  correct(itemId: string, value: string): Promise<InterviewState>;
  confirm(itemId: string): Promise<InterviewState>;
  reject(itemId: string): Promise<InterviewState>;
  decide(recommendationId: string, decision: NonNullable<Recommendation['decision']>): Promise<InterviewState>;
  inviteConsultant(): Promise<InterviewState>;
  review(): Promise<InterviewState>;
}

/** Couverture affichée à la place d'un « question n sur N » (POC §10.7). */
export function topics(state: InterviewState): { done: DomainKey[]; current: DomainKey[]; todo: DomainKey[] } {
  const done: DomainKey[] = [];
  const current: DomainKey[] = [];
  const todo: DomainKey[] = [];
  for (const domain of state.readiness.domains) {
    if (domain.domain === state.focus) current.push(domain.domain);
    else if (domain.score >= 75 && domain.blocking.length === 0) done.push(domain.domain);
    else todo.push(domain.domain);
  }
  return { done, current, todo };
}
